import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult } from '@synergy/api';
import { Category, DefaultMapping, Locale, LocalizedString } from '@synergy/models';
import { Dispatch } from 'redux';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';
import { CategoryFormikValues } from '../../features/configuration/data/models/category-formik-values';
import { CategoryRequest } from '../api/models/category-request';
import { localesSelector } from '../selectors/locale-selectors';

export const START_CREATE_CATEGORY = 'START_CREATE_CATEGORY';
export const PATCH_CATEGORY_COMPLETED = 'PATCH_CATEGORY_COMPLETED';
export const CATEGORY_VISIBILITY_CHANGED = 'CATEGORY_VISIBILITY_CHANGED';
export const START_PATCH_CATEGORY = 'START_PATCH_CATEGORY';
export const CANCEL_CREATE_CATEGORY = 'CANCEL_CREATE_CATEGORY';
export const CREATE_CATEGORY_COMPLETED = 'CREATE_CATEGORY_COMPLETED';
export const REQUEST_CATEGORIES = 'REQUEST_CATEGORIES';
export const RECEIVE_CATEGORIES = 'RECEIVE_CATEGORIES';
export const DELETE_CATEGORY_COMPLETED = 'DELETE_CATEGORY_COMPLETED';
export const START_DELETE_CATEGORY = 'START_DELETE_CATEGORY';
export const CANCEL_PATCH_CATEGORY = 'CANCEL_PATCH_CATEGORY';
export const CANCEL_DELETE_CATEGORY = 'CANCEL_DELETE_CATEGORY';
export const RECEIVE_CATEGORY_DEFAULT_MAPPING = 'RECEIVE_CATEGORY_DEFAULT_MAPPING';
export const RECEIVE_CATEGORY_DEFAULT_MAPPINGS = 'RECEIVE_CATEGORY_DEFAULT_MAPPINGS';
export const RECEIVE_ALL_CATEGORIES = 'RECEIVE_ALL_CATEGORIES';

export const requestCategories = () => {
    return {
        type: REQUEST_CATEGORIES,
    };
};

export const receiveCategories = (categories: Category[], locales: Locale[]) => {
    return {
        type: RECEIVE_CATEGORIES,
        categories,
        locales,
    };
};

export const receiveAllCategories = (categories: Category[], parentId?: number) => {
    return {
        type: RECEIVE_ALL_CATEGORIES,
        categories,
        parentId,
    };
};

export const receiveCategoryMapping = (defaultMapping: DefaultMapping<Category>) => {
    return {
        type: RECEIVE_CATEGORY_DEFAULT_MAPPING,
        defaultMapping,
    };
};

export const receiveCategoryMappings = (defaultMappings: DefaultMapping<Category>[]) => {
    return {
        type: RECEIVE_CATEGORY_DEFAULT_MAPPINGS,
        defaultMappings,
    };
};

const startCreate = () => {
    return { type: START_CREATE_CATEGORY };
};

const createCompleted = (category: Category, locales: Locale[]) => {
    return {
        type: CREATE_CATEGORY_COMPLETED,
        category,
        locales,
    };
};

const cancelCreate = () => {
    return { type: CANCEL_CREATE_CATEGORY };
};

const deleteCompleted = (categoryId: number, locales: Locale[]) => {
    return {
        type: DELETE_CATEGORY_COMPLETED,
        categoryId,
        locales,
    };
};

const startDelete = () => {
    return {
        type: START_DELETE_CATEGORY,
    };
};

const cancelDelete = () => {
    return {
        type: CANCEL_DELETE_CATEGORY,
    };
};

const startPatch = () => {
    return { type: START_PATCH_CATEGORY };
};

const patchCompleted = (category: Category, locales: Locale[]) => {
    return {
        type: PATCH_CATEGORY_COMPLETED,
        category,
        locales,
    };
};

const visibilityChanged = (categoryId: number, isVisibleInShop: boolean, locales: Locale[]) => {
    return {
        type: CATEGORY_VISIBILITY_CHANGED,
        categoryId,
        isVisibleInShop,
        locales,
    };
};

const cancelPatch = () => {
    return { type: CANCEL_PATCH_CATEGORY };
};

export const fetchCategories = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestCategories());

        const response = await api.categories.cgetCategories();

        if (response.success) {
            dispatch(receiveCategories(response.result.items, localesSelector(getState())));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const getAllCategories = (parentId?: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestCategories());

        const response = await api.categories.getAllCategories(parentId);

        if (response.success) {
            dispatch(receiveAllCategories(response.result, parentId));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchCategoriesMapping = () => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestCategories());

        const response = await api.categories.getDefaultMapping();

        if (response.success) {
            dispatch(receiveCategoryMappings(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteCategory = (categoryId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startDelete());

        const response = await api.categories.deleteCategory(categoryId);

        if (response.success === true) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.configuration.deleteCategory.success')));
            dispatch(deleteCompleted(categoryId, localesSelector(getState())));
        } else {
            dispatch(cancelDelete());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const patchCategory = (category: Category) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startPatch());
        const id = category.id;
        const data: CategoryRequest = {
            eclassPositions: category.eclassPositions?.map(eclass => eclass) ?? [],
            parentId: category.parentId,
            translations: category.translations,
        };
        const response = await api.categories.updateCategory(id, data);

        if (response.success === true) {
            dispatch(patchCompleted(response.result, localesSelector(getState())));
        } else {
            dispatch(cancelPatch());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const createCategory = (category: CategoryFormikValues) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startCreate());

        const data: CategoryRequest = {
            parentId: category.parentId,
            translations: category.translations as LocalizedString,
            eclassPositions: category.eclasses?.map(eclass => eclass.id) ?? [],
        };
        const response = await api.categories.sendNewCategory(data);

        if (response.success === true) {
            dispatch(createCompleted(response.result, localesSelector(getState())));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.category.created')));
        } else {
            dispatch(cancelCreate());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const updateDefaultMapping = (mapping: DefaultMapping) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startPatch());
        const response = await api.categories.updateDefaultMapping(mapping);

        if (response.success === true) {
            dispatch(receiveCategoryMapping(response.result));
        } else {
            dispatch(cancelPatch());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleCategoryVisibility = (category: Category) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(visibilityChanged(category.id, !category.isVisibleInShop, localesSelector(getState())));

        let response: ApiResult<null>;
        if (category.isVisibleInShop) {
            response = await api.categories.hideCategory(category.id);
        } else {
            response = await api.categories.showCategory(category.id);
        }

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.category.visibility.updated')));
        } else {
            dispatch(cancelPatch());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
